import React from 'react';
import {
    FONT_SIZE,
    getPhysicalRatio,
    getTickGap,
    MIN_LAST_GAP,
    RULER_BAR_SIZE,
    SHORT_TICK_OFFSET,
    SHORT_TICK_SIZE,
    TEXT_OFFSET,
} from '../constants';
import type { RulerUnit } from '../types';

export function drawVerticalRuler({
    scale,
    rulerUnit,
    paddingTop,
    scrollTop,
    viewHeight,
    stageSize,
    verticalRulerRef,
}: {
    scale: number;
    rulerUnit: RulerUnit;
    paddingTop: number;
    scrollTop: number;
    viewHeight: number;
    stageSize: { width: number; height: number };
    verticalRulerRef: React.RefObject<HTMLCanvasElement>;
}) {
    const canvas = verticalRulerRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (scale <= 0) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
    }

    const TICK_COLOR = getComputedStyle(document.body).getPropertyValue('--canvas-ruler-tick-color');
    const TEXT_COLOR = getComputedStyle(document.body).getPropertyValue('--canvas-ruler-text-color');

    const tickGap = getTickGap(scale, rulerUnit);
    const physicalRatio = getPhysicalRatio(rulerUnit);

    const canvasWidth = RULER_BAR_SIZE;
    const canvasHeight = viewHeight - RULER_BAR_SIZE;

    const dpi = window.devicePixelRatio || 1;

    canvas.width = canvasWidth * dpi;
    canvas.height = canvasHeight * dpi;

    // 解决高清屏模糊
    ctx.scale(dpi, dpi);

    // 由于垂直标尺是旋转 90 度绘制的，所以需要反向计算
    const top = paddingTop - RULER_BAR_SIZE - scrollTop;
    const end = canvasHeight - top;
    const start = end - stageSize.height * scale;
    const physicalHeight = stageSize.height * physicalRatio;
    // 旋转画布
    ctx.translate(0, canvasHeight);
    ctx.rotate(-Math.PI / 2);

    // 绘制首尾的刻度
    ctx.fillStyle = TICK_COLOR;
    ctx.fillRect(start, 0, 1, RULER_BAR_SIZE - 1);
    ctx.fillRect(end, 0, 1, RULER_BAR_SIZE - 1);

    // 绘制首尾的数值
    ctx.font = FONT_SIZE;
    ctx.fillStyle = TEXT_COLOR;
    ctx.textAlign = 'right';
    ctx.fillText(`${physicalHeight.toFixed(0)} `, start, TEXT_OFFSET);
    ctx.textAlign = 'left';
    ctx.fillText(' 0', end, TEXT_OFFSET);

    ctx.textAlign = 'center';
    // 绘制刻度 和 刻度上的数值
    const scaleGap = (tickGap * scale) / physicalRatio;
    const lastPos = start + scaleGap + MIN_LAST_GAP;
    // 从尾部开始绘制
    for (let i = 1, pos = end; pos > lastPos; i++) {
        pos = end - i * scaleGap;
        ctx.fillStyle = TICK_COLOR;
        ctx.fillRect(pos, SHORT_TICK_OFFSET, 1, SHORT_TICK_SIZE);
        ctx.fillStyle = TEXT_COLOR;
        ctx.fillText(`${i * tickGap}`, pos, TEXT_OFFSET);
    }
}

export function drawHorizontalRuler({
    scale,
    rulerUnit,
    paddingLeft,
    scrollLeft,
    viewWidth,
    stageSize,
    horizontalRulerRef,
}: {
    scale: number;
    rulerUnit: RulerUnit;
    paddingLeft: number;
    scrollLeft: number;
    viewWidth: number;
    stageSize: { width: number; height: number };
    horizontalRulerRef: React.RefObject<HTMLCanvasElement>;
}) {
    const canvas = horizontalRulerRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (scale <= 0) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
    }

    const TICK_COLOR = getComputedStyle(document.body).getPropertyValue('--canvas-ruler-tick-color');
    const TEXT_COLOR = getComputedStyle(document.body).getPropertyValue('--canvas-ruler-text-color');

    const tickGap = getTickGap(scale, rulerUnit);
    const physicalRatio = getPhysicalRatio(rulerUnit);

    const dpi = window.devicePixelRatio || 1;
    canvas.width = (viewWidth - RULER_BAR_SIZE) * dpi;
    canvas.height = RULER_BAR_SIZE * dpi;

    // 解决高清屏模糊
    ctx.scale(dpi, dpi);

    const left = paddingLeft - RULER_BAR_SIZE - scrollLeft;
    const start = left;
    const end = left + stageSize.width * scale;
    const physicalWidth = stageSize.width * physicalRatio;

    // 绘制首尾的刻度
    ctx.fillStyle = TICK_COLOR;
    ctx.fillRect(start, 0, 1, RULER_BAR_SIZE - 1);
    ctx.fillRect(end, 0, 1, RULER_BAR_SIZE - 1);

    // 绘制首尾的数值
    ctx.font = FONT_SIZE;
    ctx.fillStyle = TEXT_COLOR;
    ctx.textAlign = 'right';
    ctx.fillText('0 ', start, TEXT_OFFSET);
    ctx.textAlign = 'left';
    ctx.fillText(` ${physicalWidth.toFixed(0)}`, end, TEXT_OFFSET);

    ctx.textAlign = 'center';

    // 绘制刻度 和 刻度上的数值
    for (let i = tickGap; i < physicalWidth - MIN_LAST_GAP * physicalRatio; i += tickGap) {
        const pos = start + (i / physicalRatio) * scale;
        ctx.fillStyle = TICK_COLOR;
        ctx.fillRect(pos, SHORT_TICK_OFFSET, 1, SHORT_TICK_SIZE);
        ctx.fillStyle = TEXT_COLOR;
        ctx.fillText(`${i.toFixed(0)}`, pos, TEXT_OFFSET);
    }
}
